/**
 * IndexHealthAdvisor — Tools > Index & PK Health Advisor.
 *
 * Read-only audit of one connection (or one database): tables with no PRIMARY
 * KEY, redundant (prefix-covered) indexes, exact duplicates, and best-effort
 * unused indexes. Findings marked "safe" (non-unique, non-PRIMARY) can be
 * dropped in one click — but the SERVER re-introspects and only drops indexes
 * its own rules still endorse, so this UI can never coerce an arbitrary DROP.
 * The no-PK finding is advisory only (adding a PK can fail / rewrites the table).
 *
 * Props: onClose()
 */
import { createSignal, createResource, For, Show } from 'solid-js';
import { api } from '../lib/api.js';
import Modal from './Modal.jsx';
import { analyzeIndexHealth, applyIndexDrops } from '../lib/indexhealth.js';
import { toast } from '../lib/toast.js';

const SYSTEM_DBS = ['mysql', 'information_schema', 'performance_schema', 'sys'];
const keyOf = (f) => `${f.schema} ${f.table} ${f.index}`;

const KIND_LABEL = { 'no-pk': 'No PK', duplicate: 'Duplicate', redundant: 'Redundant', unused: 'Unused' };

export default function IndexHealthAdvisor(props) {
  const [conns] = createResource(async () => {
    const { connections } = await api.get('/api/connections');
    return connections ?? [];
  });
  const [connId, setConnId]   = createSignal('');
  const [scope, setScope]     = createSignal('connection'); // 'connection' | 'database'
  const [dbList, setDbList]   = createSignal(null);
  const [dbLoading, setDbLoading] = createSignal(false);
  const [database, setDatabase]   = createSignal('');
  const [analyzing, setAnalyzing] = createSignal(false);
  const [err, setErr]         = createSignal('');
  const [findings, setFindings] = createSignal(null); // null = not analyzed yet
  const [scanned, setScanned] = createSignal({ tables: 0, indexes: 0 });
  const [picked, setPicked]   = createSignal(new Set());
  const [kindFilter, setKindFilter] = createSignal(''); // '' = all
  const [confirmRun, setConfirmRun] = createSignal(false);
  const [running, setRunning] = createSignal(false);
  const [runResults, setRunResults] = createSignal(null);

  const connName = () => conns()?.find((c) => String(c.id) === String(connId()))?.name || '';

  const loadDbs = async (id) => {
    if (!id) { setDbList(null); return; }
    setDbLoading(true);
    try {
      const { databases } = await api.get(`/api/connections/${id}/databases`);
      setDbList((databases ?? []).map((d) => d.name).filter((n) => !SYSTEM_DBS.includes(n)));
    } catch (e) { setErr(e.message); setDbList([]); }
    finally { setDbLoading(false); }
  };

  const onConnChange = (id) => {
    setConnId(id); setDatabase(''); setFindings(null); setRunResults(null); setPicked(new Set());
    if (scope() === 'database') loadDbs(id);
  };
  const onScopeChange = (s) => {
    setScope(s);
    if (s === 'database' && connId()) loadDbs(connId());
  };

  const analyze = async () => {
    if (!connId()) { setErr('Pick a connection first.'); return; }
    if (scope() === 'database' && !database()) { setErr('Pick a database.'); return; }
    setErr(''); setAnalyzing(true); setRunResults(null);
    try {
      const r = await analyzeIndexHealth(connId(), scope() === 'database' ? database() : '');
      setFindings(r.findings || []);
      setScanned(r.scanned || { tables: 0, indexes: 0 });
      // Pre-select every safe drop — the common case is "clean these up".
      const sel = new Set();
      for (const f of (r.findings || [])) if (f.safe) sel.add(keyOf(f));
      setPicked(sel);
      if (!(r.findings || []).length) toast.success('No index or primary-key issues found. 🎉');
    } catch (e) { setErr(e.message); }
    finally { setAnalyzing(false); }
  };

  const counts = () => {
    const c = { 'no-pk': 0, duplicate: 0, redundant: 0, unused: 0 };
    for (const f of (findings() || [])) c[f.kind] = (c[f.kind] || 0) + 1;
    return c;
  };
  const safeFindings = () => (findings() || []).filter((f) => f.safe);
  const visible = () => {
    const k = kindFilter();
    return (findings() || []).filter((f) => !k || f.kind === k);
  };

  const togglePick = (f) => {
    if (!f.safe) return;
    const s = new Set(picked());
    const k = keyOf(f);
    s.has(k) ? s.delete(k) : s.add(k);
    setPicked(s);
  };
  const allSafePicked = () => safeFindings().length > 0 && safeFindings().every((f) => picked().has(keyOf(f)));
  const toggleAllSafe = () => {
    if (allSafePicked()) { setPicked(new Set()); return; }
    const s = new Set();
    for (const f of safeFindings()) s.add(keyOf(f));
    setPicked(s);
  };

  const pickedDrops = () =>
    (findings() || []).filter((f) => f.safe && picked().has(keyOf(f)))
      .map((f) => ({ schema: f.schema, table: f.table, index: f.index }));

  const copySql = async (sql) => {
    try { await navigator.clipboard.writeText(sql); toast.success('Copied.'); }
    catch { toast.warn('Copy failed — select the text manually.'); }
  };
  const copyAllPicked = async () => {
    const sql = (findings() || []).filter((f) => f.safe && picked().has(keyOf(f))).map((f) => f.sql).join('\n');
    if (!sql) { toast.info('Nothing selected.'); return; }
    copySql(sql);
  };

  const runDrops = async () => {
    setConfirmRun(false);
    const drops = pickedDrops();
    if (!drops.length) { toast.info('No indexes selected.'); return; }
    setRunning(true); setRunResults(null);
    try {
      const r = await applyIndexDrops(connId(), drops, 'continue');
      setRunResults(r);
      if (r.ok && !r.skipped) toast.success(`Dropped ${r.ran - r.failed} index(es).`);
      else toast.warn(`${r.ran - r.failed} dropped · ${r.failed} failed · ${r.skipped} skipped.`);
      analyze(); // re-scan so dropped indexes disappear and the picture refreshes
    } catch (e) { toast.error(`Apply failed: ${e.message}`); }
    finally { setRunning(false); }
  };

  return (
    <Modal open={true} onClose={props.onClose} title="Index & PK Health Advisor" wide>
      <div class="col-renamer">
        <p class="cr-intro muted">
          Audit a connection for tables with no primary key, redundant or duplicate indexes, and
          unused indexes. Safe cleanups (dropping a redundant/duplicate/unused secondary index) can be
          applied in one click — the server re-checks each before dropping. Adding a primary key is advisory only.
        </p>

        <section class="cr-step">
          <header class="cr-step-head">
            <span class="cr-step-num">1</span>
            <span class="cr-step-title">Pick the source</span>
          </header>
          <div class="cr-step-body">
            <div class="cr-grid">
              <label class="cr-field">
                <span class="cr-field-label">Connection</span>
                <select class="input" value={connId()} onChange={(e) => onConnChange(e.currentTarget.value)}>
                  <option value="">— pick a connection —</option>
                  <For each={conns() ?? []}>{(c) => <option value={c.id}>{c.name}</option>}</For>
                </select>
              </label>
              <div class="cr-field">
                <span class="cr-field-label">Scope</span>
                <div class="cr-scope-row">
                  <label class="cr-radio" classList={{ active: scope() === 'connection' }}>
                    <input type="radio" name="ih-scope" checked={scope() === 'connection'}
                           onChange={() => onScopeChange('connection')} />
                    <span>Whole connection</span>
                    <small class="muted">all databases except system</small>
                  </label>
                  <label class="cr-radio" classList={{ active: scope() === 'database' }}>
                    <input type="radio" name="ih-scope" checked={scope() === 'database'}
                           onChange={() => onScopeChange('database')} />
                    <span>Single database</span>
                    <small class="muted">just one</small>
                  </label>
                </div>
              </div>
              <Show when={scope() === 'database'}>
                <label class="cr-field">
                  <span class="cr-field-label">Database</span>
                  <select class="input" value={database()} onChange={(e) => setDatabase(e.currentTarget.value)}
                          disabled={dbLoading() || !dbList()}>
                    <option value="">{dbLoading() ? 'loading…' : '— pick a database —'}</option>
                    <For each={dbList() ?? []}>{(n) => <option value={n}>{n}</option>}</For>
                  </select>
                </label>
              </Show>
            </div>
            <div class="cr-step-actions">
              <button class="btn" onClick={analyze} disabled={analyzing() || !connId()}>
                {analyzing() ? '⏳ Scanning…' : '🔎 Analyze'}
              </button>
            </div>
          </div>
        </section>

        <Show when={err()}>
          <div class="form-error" style="margin:8px 0;">{err()}</div>
        </Show>

        <Show when={findings() !== null}>
          <section class="cr-step">
            <header class="cr-step-head">
              <span class="cr-step-num">2</span>
              <span class="cr-step-title">Findings</span>
              <span class="cr-step-meta muted">
                scanned {scanned().tables} tables · {scanned().indexes} indexes · {(findings() || []).length} finding{(findings() || []).length === 1 ? '' : 's'}
              </span>
            </header>
            <div class="cr-step-body">
              <Show when={(findings() || []).length === 0}>
                <div class="ih-clean">✓ No primary-key or index issues found in this scope.</div>
              </Show>

              <Show when={(findings() || []).length > 0}>
                <div class="ih-chips">
                  <button class={`day-chip ${kindFilter() === '' ? 'on' : ''}`} onClick={() => setKindFilter('')}>
                    All ({(findings() || []).length})
                  </button>
                  <For each={['no-pk', 'duplicate', 'redundant', 'unused']}>
                    {(k) => (
                      <Show when={counts()[k] > 0}>
                        <button class={`day-chip ih-chip-${k} ${kindFilter() === k ? 'on' : ''}`} onClick={() => setKindFilter(k)}>
                          {KIND_LABEL[k]} ({counts()[k]})
                        </button>
                      </Show>
                    )}
                  </For>
                </div>

                <div class="ih-toolbar">
                  <label class="ih-selall" title="Select every safe-to-drop index">
                    <input type="checkbox" checked={allSafePicked()} disabled={!safeFindings().length}
                           onInput={toggleAllSafe} />
                    Select all safe drops ({safeFindings().length})
                  </label>
                  <div class="spacer" />
                  <button class="btn btn-ghost btn-sm" onClick={copyAllPicked} disabled={!picked().size}>⧉ Copy selected SQL</button>
                  <button class="btn btn-danger-solid btn-sm" onClick={() => setConfirmRun(true)}
                          disabled={running() || !picked().size}>
                    {running() ? '⏳ Dropping…' : `🗑 Drop selected (${picked().size})`}
                  </button>
                </div>

                <Show when={confirmRun()}>
                  <div class="cr-confirm">
                    <span>⚠ Drop {picked().size} index(es) on <strong>{connName()}</strong>? Each is re-verified server-side and audit-logged.</span>
                    <div class="spacer" />
                    <button class="btn btn-sm btn-ghost" onClick={() => setConfirmRun(false)}>Cancel</button>
                    <button class="btn btn-sm btn-danger-solid" onClick={runDrops}>Confirm drop</button>
                  </div>
                </Show>

                <div class="ih-table-wrap">
                  <table class="ih-table">
                    <thead>
                      <tr>
                        <th class="ih-col-check"></th>
                        <th>Table</th>
                        <th>Index</th>
                        <th>Columns</th>
                        <th>Issue</th>
                        <th></th>
                      </tr>
                    </thead>
                    <tbody>
                      <For each={visible()}>
                        {(f) => (
                          <tr class={`ih-row ih-row-${f.kind}`}>
                            <td class="ih-col-check">
                              <Show when={f.safe} fallback={<span class="ih-advisory" title="Advisory only — apply manually">·</span>}>
                                <input type="checkbox" checked={picked().has(keyOf(f))} onInput={() => togglePick(f)} />
                              </Show>
                            </td>
                            <td class="mono ih-tablecell">{f.schema}.{f.table}</td>
                            <td class="mono">
                              <Show when={f.index} fallback={<span class="muted">—</span>}>{f.index}</Show>
                            </td>
                            <td class="mono ih-cols">
                              <Show when={(f.columns || []).length}>({(f.columns || []).join(', ')})</Show>
                            </td>
                            <td>
                              <span class={`ih-badge ih-badge-${f.kind}`}>{KIND_LABEL[f.kind]}</span>
                              <span class="ih-detail muted"> {f.detail}</span>
                            </td>
                            <td class="ih-col-copy">
                              <button class="btn btn-ghost btn-xs" title="Copy the suggested SQL" onClick={() => copySql(f.sql)}>⧉</button>
                            </td>
                          </tr>
                        )}
                      </For>
                    </tbody>
                  </table>
                </div>

                <Show when={runResults()}>
                  <div class="cr-results">
                    <div class="muted" style="margin:6px 0 4px;">
                      {runResults().ran - runResults().failed} dropped · {runResults().failed} failed · {runResults().skipped} skipped
                    </div>
                    <For each={runResults().results.filter((x) => !x.ok)}>
                      {(x) => <div class="cr-result-err mono"><span>✗</span> {x.schema}.{x.table}.{x.index}: {x.error}</div>}
                    </For>
                  </div>
                </Show>

                <p class="cr-footnote muted">
                  <strong>No PK</strong> is advisory — adding a primary key can fail on duplicate/NULL data and
                  rewrites the table, so it's never auto-applied. <strong>Unused</strong> relies on Performance Schema
                  counters since the server last started — verify the index really is idle before dropping.
                </p>
              </Show>
            </div>
          </section>
        </Show>
      </div>
    </Modal>
  );
}
